import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Ban, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatTime } from '../i18n';
import {
  dayKey, addDaysToKey, dayRange, instantAt, fromInputValue,
} from '../lib/practiceTime';
import { Card, Button, Input, Select, Field, Modal, Alert, Badge } from './ui';

/**
 * Time a professional does not want booked.
 *
 * The table and the rules have been there since the first schema — both
 * book_appointment and available_slots refuse a slot that overlaps a block —
 * but nothing ever wrote to it, so the feature existed everywhere except where
 * somebody could use it.
 *
 * Blocking does NOT cancel appointments already in the range. A booking is an
 * agreement with a person; making it vanish because the professional shaded out
 * an afternoon would be the wrong default, and they may well intend to keep one
 * of them. So the form counts what is already there and says so, and cancelling
 * stays a deliberate act in the agenda.
 */

// Every date and time here is on the practice's clock (lib/practiceTime.js):
// `from` and `to` are day keys, and what is typed into the form is read as the
// practice's wall clock rather than the browser's.
const emptyDraft = (key) => ({
  from: `${key}T09:00`,
  to: `${key}T13:00`,
  allDay: false,
  reason: '',
  kind: 'personal',
});

const INVALID = new Date(NaN);

const ScheduleBlocks = ({ therapistId, from, to, onChange }) => {
  const [blocks, setBlocks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft(from));
  const [collisions, setCollisions] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!therapistId) return;
    // Anything overlapping the day on screen, including a block that started
    // last week and runs through it.
    const { data, error: loadError } = await supabase
      .from('therapist_schedule_blocks')
      .select('id, starts_at, ends_at, reason, block_type')
      .eq('therapist_id', therapistId)
      .lt('starts_at', dayRange(to).end)
      .gt('ends_at', dayRange(from).start)
      .order('starts_at');
    if (loadError) { setError(loadError.message); return; }
    setBlocks(data ?? []);
  }, [therapistId, from, to]);

  useEffect(() => { load(); }, [load]);

  const range = useMemo(() => {
    if (draft.allDay) {
      // Whole days: from the first midnight to the midnight after the last day.
      const first = draft.from.slice(0, 10);
      const last = (draft.to || draft.from).slice(0, 10);
      if (!first || !last) return { from: INVALID, to: INVALID };
      return { from: instantAt(first), to: instantAt(addDaysToKey(last, 1)) };
    }
    return { from: fromInputValue(draft.from) ?? INVALID, to: fromInputValue(draft.to) ?? INVALID };
  }, [draft]);

  // Recount whenever the range moves, so the warning is about what they are
  // actually about to block rather than what they first typed.
  useEffect(() => {
    if (!adding || !therapistId) return;
    if (Number.isNaN(range.from.getTime()) || Number.isNaN(range.to.getTime())) return;
    if (range.to <= range.from) { setCollisions(0); return; }

    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('therapist_id', therapistId)
        // A request waiting for approval holds its time too (0055).
        .in('status', ['requested', 'scheduled', 'waiting', 'in_session'])
        .lt('starts_at', range.to.toISOString())
        .gt('ends_at', range.from.toISOString());
      if (!cancelled) setCollisions(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [adding, therapistId, range]);

  const open = () => {
    // Defaults to the first day on screen rather than today, so opening the
    // form from a week in view does not silently propose this morning.
    setDraft(emptyDraft(from));
    setCollisions(0);
    setError(null);
    setAdding(true);
  };

  const save = async () => {
    if (Number.isNaN(range.from.getTime()) || Number.isNaN(range.to.getTime()) || range.to <= range.from) {
      setError(t('blocks.endBeforeStart'));
      return;
    }
    setBusy(true);
    const { error: saveError } = await supabase.from('therapist_schedule_blocks').insert([{
      therapist_id: therapistId,
      starts_at: range.from.toISOString(),
      ends_at: range.to.toISOString(),
      reason: draft.reason.trim() || null,
      block_type: draft.kind,
    }]);
    setBusy(false);
    if (saveError) { setError(saveError.message); return; }
    setAdding(false);
    setNotice(t('blocks.saved'));
    load();
    onChange?.();
  };

  const remove = async (block) => {
    if (!window.confirm(t('blocks.confirmRemove'))) return;
    const { error: removeError } = await supabase
      .from('therapist_schedule_blocks').delete().eq('id', block.id);
    if (removeError) { setError(removeError.message); return; }
    setNotice(t('blocks.removed'));
    load();
    onChange?.();
  };

  /** A block confined to the day on screen reads as times; a longer one needs dates. */
  const label = (block) => {
    const startsAt = new Date(block.starts_at);
    const endsAt = new Date(block.ends_at);
    if (dayKey(startsAt) === dayKey(endsAt)) {
      return `${formatDate(startsAt, { weekday: 'short', day: 'numeric', month: 'short' })}`
        + ` · ${formatTime(startsAt)} – ${formatTime(endsAt)}`;
    }
    return t('blocks.spansDays', {
      from: formatDate(startsAt, { day: 'numeric', month: 'short' }),
      to: formatDate(endsAt, { day: 'numeric', month: 'short' }),
    });
  };

  return (
    <Card
      title={t('blocks.title')}
      subtitle={t('blocks.subtitle')}
      actions={<Button variant="secondary" onClick={open}><Plus size={14} />{t('blocks.add')}</Button>}
    >
      {error && !adding && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {blocks.length === 0 ? (
        <p className="text-sm text-slate-500">{t('blocks.none')}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {blocks.map((block) => (
            <li key={block.id} className="flex items-center justify-between gap-3 py-2">
              <div className="flex items-center gap-2">
                <Ban size={14} className="shrink-0 text-slate-400" />
                <span className="text-sm font-medium text-slate-800">{label(block)}</span>
                <Badge tone="neutral">{t(`blocks.kinds.${block.block_type}`)}</Badge>
                {block.reason && <span className="text-sm text-slate-500">{block.reason}</span>}
              </div>
              <Button variant="ghost" onClick={() => remove(block)} title={t('common.delete')}>
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={adding}
        title={t('blocks.add')}
        onClose={() => setAdding(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
            <Button onClick={save} loading={busy}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          {error && <Alert tone="danger">{error}</Alert>}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.allDay}
              onChange={(event) => setDraft({ ...draft, allDay: event.target.checked })}
            />
            {t('blocks.allDay')}
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('blocks.from')} required>
              <Input
                type={draft.allDay ? 'date' : 'datetime-local'}
                value={draft.allDay ? draft.from.slice(0, 10) : draft.from}
                onChange={(event) => setDraft({
                  ...draft,
                  from: draft.allDay ? `${event.target.value}T00:00` : event.target.value,
                })}
              />
            </Field>
            <Field label={t('blocks.to')} required>
              <Input
                type={draft.allDay ? 'date' : 'datetime-local'}
                value={draft.allDay ? draft.to.slice(0, 10) : draft.to}
                onChange={(event) => setDraft({
                  ...draft,
                  to: draft.allDay ? `${event.target.value}T00:00` : event.target.value,
                })}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('blocks.kind')}>
              <Select value={draft.kind}
                onChange={(event) => setDraft({ ...draft, kind: event.target.value })}>
                <option value="personal">{t('blocks.kinds.personal')}</option>
                <option value="vacation">{t('blocks.kinds.vacation')}</option>
                <option value="meeting">{t('blocks.kinds.meeting')}</option>
                <option value="blocked">{t('blocks.kinds.blocked')}</option>
              </Select>
            </Field>
            <Field label={t('blocks.reason')}>
              <Input
                value={draft.reason}
                placeholder={t('blocks.reasonPlaceholder')}
                onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
              />
            </Field>
          </div>

          {collisions > 0 && (
            <Alert tone="warning">
              {t('blocks.overlapsAppointments', { count: collisions })}
            </Alert>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default ScheduleBlocks;
